import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Spinner } from '@parsina/framework';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { C } from '../../constants/theme';
import { STACK, ROW_PAD } from '../../constants/density.js';
import MoneyInput from '../common/MoneyInput';
import { PAYMENT_METHODS, DEFAULT_PAYMENT_METHOD } from '../../lib/billing';
import { suggestedDepositRial } from '../../lib/deposit';
import { newDepositRequestId } from '../../hooks/useAppointmentDeposit';

/**
 * «پیش‌پرداخت» on «ثبت نوبت» (PM-0657, contract §6.2).
 *
 * Two pieces, shared by the two booking forms the desk has — the service-first
 * `BookingDialog` and F4's `BookAppointmentModal` → `BookingModal` — so the rule
 * of the block lives once:
 *
 *   • `useBookingDeposit` — the block's state: shown only when the clinic's
 *     policy is not OFF (and the caller says it applies: not an edit, not an
 *     order's session, not «ثبت سریع»), OPEN by itself when REQUIRED and behind
 *     «+ پیش‌پرداخت» when OPTIONAL, the amount prefilled from
 *     `suggestedDepositRial` until the desk types over it, and ONE request id
 *     for the life of the form so a retry never takes the money twice.
 *   • `BookingDepositBlock` — the fields.
 *   • `DepositAfterBookingFailed` — the booking stood and the deposit did not:
 *     the reason, «تلاش دوباره» (the deposit alone, same request id) and
 *     «بستن بدون پیش‌پرداخت».
 *
 * «الزامی» is a CONFIRM, never a refusal (C9): booking without a deposit is
 * asked about once and then allowed.
 */
export function useBookingDeposit({ policy, priceRial, applies = true }) {
  const { parseMoney, toDisplayAmount } = useTranslation();
  const mode = policy?.mode || 'OFF';
  const shown = !!applies && mode !== 'OFF';
  const required = shown && mode === 'REQUIRED';
  const [opened, setOpened] = useState(false);
  const open = shown && (required || opened);

  const suggested = suggestedDepositRial(policy, priceRial);
  const [amountText, setAmountTextRaw] = useState('');
  const [edited, setEdited] = useState(false);
  useEffect(() => {
    if (edited) return;
    setAmountTextRaw(suggested ? String(toDisplayAmount(suggested)) : '');
  }, [suggested, edited, toDisplayAmount]);
  const setAmountText = (v) => { setEdited(true); setAmountTextRaw(v); };

  const [method, setMethod] = useState(DEFAULT_PAYMENT_METHOD);
  const [reference, setReference] = useState('');
  const [requestId, setRequestId] = useState(() => newDepositRequestId('booking-deposit'));
  // After a deposit was RECORDED, a form that stays open (the row's dialog) must
  // not send the same id again: accounting answers a repeated id with the first
  // write, so a second deposit would be cash taken with nothing recorded.
  const reset = () => {
    setRequestId(newDepositRequestId('booking-deposit'));
    setEdited(false);
    setReference('');
  };

  const amountRial = open && amountText !== '' ? (parseMoney(amountText) || 0) : 0;
  const wire = amountRial > 0
    ? { amountRial, paymentMethod: method, referenceNumber: reference.trim() || null, requestId }
    : null;

  return {
    mode, shown, required, open, setOpen: setOpened,
    amountText, setAmountText, method, setMethod, reference, setReference,
    amountRial, wire, requestId, reset,
    // REQUIRED and nothing typed → the form asks once before booking (C9).
    needsConfirm: required && !(amountRial > 0),
  };
}

/** The reference a non-cash deposit needs, or '' when it is fine to send. */
export function depositFieldError(t, deposit) {
  if (!deposit?.wire) return '';
  const meta = PAYMENT_METHODS(t)[deposit.method] || {};
  if (meta.needsRef && !String(deposit.reference || '').trim()) return meta.refLabel;
  return '';
}

const FIELD = {
  width: '100%', boxSizing: 'border-box', padding: '7px 9px', borderRadius: 7,
  border: `1px solid ${C.border}`, fontSize: '0.8125rem', fontFamily: 'inherit',
  color: C.textPrimary, background: 'var(--p-surface, #fff)',
};
const LABEL = { fontSize: '0.71875rem', fontWeight: 700, color: C.textMuted, marginBottom: 4, display: 'block' };

export function BookingDepositBlock({ deposit, error = '', onTouch, style }) {
  const { t, moneyUnit } = useTranslation();
  const methods = useMemo(() => PAYMENT_METHODS(t), [t]);
  if (!deposit?.shown) return null;
  const touch = () => onTouch?.();
  const meta = methods[deposit.method] || {};

  if (!deposit.open) {
    return (
      <div style={style}>
        <button
          type="button"
          data-testid="booking-deposit-add"
          onClick={() => { deposit.setOpen(true); touch(); }}
          style={{
            border: 'none', background: 'none', padding: 0, fontFamily: 'inherit',
            fontSize: '0.78125rem', fontWeight: 700, color: 'var(--p-navy)', cursor: 'pointer',
          }}
        >
          {t('reception.booking.deposit.add')}
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="booking-deposit"
      data-mode={deposit.mode}
      style={{
        display: 'flex', flexDirection: 'column', gap: STACK.tight,
        padding: ROW_PAD.compact, borderRadius: 10, border: `1px solid ${C.border}`, ...style,
      }}
    >
      <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: C.textPrimary }}>
        {t('reception.booking.deposit.title')}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <span style={LABEL}>{t('reception.booking.deposit.amount', { unit: moneyUnit() })}</span>
          <MoneyInput
            value={deposit.amountText}
            onChange={(v) => { deposit.setAmountText(v); touch(); }}
            aria-label={t('reception.booking.deposit.amount', { unit: moneyUnit() })}
            data-testid="booking-deposit-amount"
          />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <span style={LABEL}>{t('reception.booking.deposit.method')}</span>
          <select
            style={FIELD}
            value={deposit.method}
            onChange={(e) => { deposit.setMethod(e.target.value); touch(); }}
            aria-label={t('reception.booking.deposit.method')}
            data-testid="booking-deposit-method"
          >
            {Object.entries(methods).filter(([, m]) => m.enabled)
              .map(([key, m]) => <option key={key} value={key}>{m.label}</option>)}
          </select>
        </div>
      </div>
      {meta.needsRef ? (
        <div>
          <span style={LABEL}>{meta.refLabel || t('reception.booking.deposit.reference')}</span>
          <input
            {...LTR_TEXT}
            style={FIELD}
            value={deposit.reference}
            onChange={(e) => { deposit.setReference(e.target.value); touch(); }}
            aria-label={meta.refLabel || t('reception.booking.deposit.reference')}
            data-testid="booking-deposit-reference"
          />
        </div>
      ) : null}
      <div style={{ fontSize: '0.71875rem', color: C.textMuted, lineHeight: 1.7 }}>
        {t('reception.booking.deposit.hint')}
      </div>
      {error ? (
        <div role="alert" data-testid="booking-deposit-error" style={{ fontSize: '0.75rem', color: C.red }}>{error}</div>
      ) : null}
    </div>
  );
}

/** The booking stood; the deposit did not. Retry the deposit alone, or leave without it. */
export function DepositAfterBookingFailed({ reason, retrying, onRetry, onCloseWithout }) {
  const { t } = useTranslation();
  return (
    <div data-testid="booking-deposit-failed" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Alert variant="warning">{t('reception.booking.deposit.failedAfterBooking', { reason: reason || t('errors.generic') })}</Alert>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <Button variant="ghost" onClick={onCloseWithout} disabled={retrying} data-testid="booking-deposit-close-without">
          {t('reception.booking.deposit.closeWithout')}
        </Button>
        <Button onClick={onRetry} disabled={retrying} data-testid="booking-deposit-retry">
          {retrying ? <Spinner size="sm" /> : null}
          {t('reception.booking.deposit.retry')}
        </Button>
      </div>
    </div>
  );
}

export default BookingDepositBlock;
